import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../IconButton';
import { IconDownload } from '../icons';
import { useAuth } from '../../auth/useAuth';
import { useConfig } from '../../context/ConfigContext';
import { downloadFile } from '../../utils/download';
import { formatMoney } from '../../utils/format';

// Sibling to ReportView for reports that need subtotaled sections (financial
// statements, and any future grouped report) rather than a flat searchable/
// sortable/paginated table — see BuildsReports::groupedViewResponse() on the
// backend for the matching payload contract. Unlike ReportView, column
// headings and which columns are money-formatted come from the API response
// itself (`headings`/`money`), not a `columns` prop, since every statement's
// shape is already fully described by its own report data.
export type GroupedReportFilterKind = 'asOfDate' | 'dateRange';

export type GroupedRow = Record<string, string | number | null> & { comparative_amount?: number | null };

interface GroupedSection {
  label: string;
  rows: GroupedRow[];
  subtotal: GroupedRow | null;
}

interface GroupedReportSummaryItem {
  label: string;
  value: number;
  type: 'money' | 'percent' | 'count';
}

interface GroupedReportPage {
  sections: GroupedSection[];
  grand_total: GroupedRow | null;
  summary: GroupedReportSummaryItem[] | null;
  headings: Record<string, string>;
  money: string[];
  filters_description: string | null;
  comparative_filters_description: string | null;
}

interface GroupedReportViewProps {
  title: string;
  description?: string;
  endpoint: string;
  filters: GroupedReportFilterKind[];
  // Trial Balance has no single 'amount' column (separate debit/credit),
  // so it doesn't support a comparative-period column — see
  // ConsolidatedReportController::withComparative()'s own comment.
  supportsComparative?: boolean;
  xlsx?: boolean;
  // Cash Flow only — toggles ConsolidatedReportController::cashFlowData()'s
  // ?method=direct|indirect query param.
  methodToggle?: boolean;
}

export default function GroupedReportView({
  title,
  description,
  endpoint,
  filters,
  supportsComparative = false,
  xlsx = false,
  methodToggle = false,
}: GroupedReportViewProps) {
  const { settings } = useConfig();
  const { can } = useAuth();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  // Trial Balance rows carry an 'id' (chart_of_account_id) alongside 'code'
  // — no other statement does, so this only ever lights up there. Gated on
  // gl_interface.reports.view since that's what /reports/gl/account-ledger
  // itself requires; a consolidated.reports.view-only user would otherwise
  // see a link that 403s.
  const canDrillDown = can('gl_interface.reports.view');

  const today = new Date().toISOString().slice(0, 10);
  const [asOf, setAsOf] = useState(today);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [comparing, setComparing] = useState(false);
  const [compareAsOf, setCompareAsOf] = useState('');
  const [compareDateFrom, setCompareDateFrom] = useState('');
  const [compareDateTo, setCompareDateTo] = useState('');

  const [data, setData] = useState<GroupedReportPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [method, setMethod] = useState<'indirect' | 'direct'>('indirect');

  const filterParams = useMemo(
    () => ({
      ...(filters.includes('asOfDate')
        ? { as_of: asOf || undefined }
        : { date_from: dateFrom || undefined, date_to: dateTo || undefined }),
      ...(comparing && filters.includes('asOfDate') && { compare_as_of: compareAsOf || undefined }),
      ...(comparing &&
        !filters.includes('asOfDate') && {
          compare_date_from: compareDateFrom || undefined,
          compare_date_to: compareDateTo || undefined,
        }),
      ...(methodToggle && { method }),
    }),
    [filters, asOf, dateFrom, dateTo, comparing, compareAsOf, compareDateFrom, compareDateTo, methodToggle, method]
  );

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api
      .get<GroupedReportPage>(endpoint, { params: filterParams })
      .then((res) => setData(res.data))
      .catch((err: ApiError) => setError(err.message || 'Failed to load report.'))
      .finally(() => setLoading(false));
  }, [endpoint, filterParams]);

  useEffect(() => {
    load();
  }, [load]);

  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadFile(api, `${endpoint}/pdf`, `${title.toLowerCase().replace(/\s+/g, '-')}.pdf`, filterParams);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const exportXlsx = async () => {
    if (exportingXlsx) return;
    setExportingXlsx(true);
    try {
      await downloadFile(api, `${endpoint}/xlsx`, `${title.toLowerCase().replace(/\s+/g, '-')}.xlsx`, filterParams);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExportingXlsx(false);
    }
  };

  const columnKeys = data ? Object.keys(data.headings) : [];
  const showComparativeColumn = supportsComparative && comparing && data?.comparative_filters_description != null;

  const renderCell = (key: string, row: GroupedRow) => {
    const value = row[key];
    if (value === null || value === undefined || value === '') return <span className="muted">—</span>;
    if (data?.money.includes(key)) return fmtMoney(Number(value));
    if (key === 'code' && row.id != null && canDrillDown) {
      return <Link to={`/reports/gl/account-ledger?chart_of_account_id=${row.id}`}>{value}</Link>;
    }
    return value;
  };

  const renderComparativeCell = (row: GroupedRow) => {
    const value = row.comparative_amount;
    return value === null || value === undefined ? <span className="muted">—</span> : fmtMoney(Number(value));
  };

  return (
    <>
      <header className="page-header">
        <h1>{title}</h1>
      </header>
      {description && <p className="muted">{description}</p>}

      <div className="toolbar">
        {filters.includes('asOfDate') && (
          <label className="checkbox-inline">
            As of
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </label>
        )}
        {filters.includes('dateRange') && (
          <>
            <label className="checkbox-inline">
              From
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label className="checkbox-inline">
              To
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
          </>
        )}
        {supportsComparative && (
          <label className="checkbox-inline">
            <input type="checkbox" checked={comparing} onChange={(e) => setComparing(e.target.checked)} />
            Compare to
          </label>
        )}
        {supportsComparative &&
          comparing &&
          (filters.includes('asOfDate') ? (
            <label className="checkbox-inline">
              <input type="date" value={compareAsOf} onChange={(e) => setCompareAsOf(e.target.value)} />
            </label>
          ) : (
            <>
              <label className="checkbox-inline">
                From
                <input type="date" value={compareDateFrom} onChange={(e) => setCompareDateFrom(e.target.value)} />
              </label>
              <label className="checkbox-inline">
                To
                <input type="date" value={compareDateTo} onChange={(e) => setCompareDateTo(e.target.value)} />
              </label>
            </>
          ))}
        {methodToggle && (
          <label className="checkbox-inline">
            Method
            <select value={method} onChange={(e) => setMethod(e.target.value as 'indirect' | 'direct')}>
              <option value="indirect">Indirect</option>
              <option value="direct">Direct</option>
            </select>
          </label>
        )}
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Export PDF'}
          onClick={exportPdf}
          disabled={exporting}
        />
        {xlsx && (
          <IconButton
            icon={IconDownload}
            label={exportingXlsx ? 'Exporting…' : 'Export Excel'}
            onClick={exportXlsx}
            disabled={exportingXlsx}
          />
        )}
      </div>

      {data?.filters_description && <p className="muted">{data.filters_description}</p>}
      {showComparativeColumn && <p className="muted">Comparative: {data.comparative_filters_description}</p>}

      {data?.summary && (
        <div className="stat-grid">
          {data.summary.map((s) => (
            <div className="card stat" key={s.label}>
              <span className="stat-label">{s.label}</span>
              <span className="stat-value">
                {s.type === 'money' ? fmtMoney(s.value) : s.type === 'percent' ? `${s.value}%` : s.value}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        {loading ? (
          <p className="muted">Loading…</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : data ? (
          <>
            {data.sections.map((section) => (
              <div className="grouped-report-section" key={section.label}>
                <h3>{section.label}</h3>
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        {columnKeys.map((key) => (
                          <th key={key} className={data.money.includes(key) ? 'right' : ''}>
                            {data.headings[key]}
                          </th>
                        ))}
                        {showComparativeColumn && <th className="right">Comparative</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.length > 0 ? (
                        section.rows.map((row, i) => (
                          <tr key={i}>
                            {columnKeys.map((key) => (
                              <td key={key} className={data.money.includes(key) ? 'right' : ''}>
                                {renderCell(key, row)}
                              </td>
                            ))}
                            {showComparativeColumn && <td className="right">{renderComparativeCell(row)}</td>}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columnKeys.length + (showComparativeColumn ? 1 : 0)} className="muted">
                            No records found.
                          </td>
                        </tr>
                      )}
                      {section.subtotal && (
                        <tr className="subtotal-row">
                          {columnKeys.map((key) => (
                            <td key={key} className={data.money.includes(key) ? 'right' : ''}>
                              {renderCell(key, section.subtotal as GroupedRow)}
                            </td>
                          ))}
                          {showComparativeColumn && (
                            <td className="right">{renderComparativeCell(section.subtotal as GroupedRow)}</td>
                          )}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {data.grand_total && (
              <div className="table-scroll">
                <table>
                  <tbody>
                    <tr className="grouped-report-grand-total">
                      {columnKeys.map((key) => (
                        <td key={key} className={data.money.includes(key) ? 'right' : ''}>
                          {renderCell(key, data.grand_total as GroupedRow)}
                        </td>
                      ))}
                      {showComparativeColumn && (
                        <td className="right">{renderComparativeCell(data.grand_total as GroupedRow)}</td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
